/**
 * ComponentsListing.tsx
 * Hub for component categories - showcases our component organization
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation, Layers, MousePointer2, MessageCircle, Database, ArrowRight } from 'lucide-react';
import { PlaygroundPageLayout } from './shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';

interface ComponentCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<Record<string, unknown>>;
  componentCount: number;
  route: string;
  status: 'available' | 'coming-soon';
  features: string[];
}

const componentCategories: ComponentCategory[] = [
  {
    id: 'navigation',
    title: 'Navigation Components',
    description: 'Components for navigation and wayfinding throughout your application. Build intuitive navigation experiences.',
    icon: Navigation,
    componentCount: 5,
    route: '/playground/components/navigation',
    status: 'available',
    features: ['Link', 'Navbar', 'Breadcrumbs', 'Tabs', 'Pagination']
  },
  {
    id: 'layout',
    title: 'Layout Components', 
    description: 'Structural components for organizing and presenting content. Create beautiful layouts and visual hierarchy.',
    icon: Layers,
    componentCount: 8,
    route: '/playground/components/layout',
    status: 'available',
    features: ['Badge', 'Card', 'Chip', 'Divider', 'Drawer', 'Accordion', 'Scroll Shadow', 'Split Pane']
  },
  {
    id: 'forms',
    title: 'Form Controls',
    description: 'Interactive form elements for user input and data collection. Build powerful forms with validation.',
    icon: MousePointer2,
    componentCount: 10,
    route: '/playground/components/forms',
    status: 'available',
    features: ['Button', 'Input', 'Select', 'Checkbox', 'RadioGroup', 'TextArea', 'NumberInput', 'Dropdown', 'Listbox', 'ButtonGroup']
  },
  {
    id: 'feedback',
    title: 'Feedback & Overlays',
    description: 'Components for user feedback, notifications, and contextual information. Enhance user communication.',
    icon: MessageCircle,
    componentCount: 9,
    route: '/playground/components/feedback',
    status: 'available',
    features: ['Modal', 'Alert', 'Toast', 'Tooltip', 'Popover', 'Circular Progress', 'Spinner', 'Progress', 'Skeleton']
  },
  {
    id: 'data-display',
    title: 'Data Display',
    description: 'Components for displaying data, content, and information in organized, accessible formats.',
    icon: Database,
    componentCount: 6,
    route: '/playground/components/data-display',
    status: 'available',
    features: ['Kbd', 'Table', 'Avatar', 'Code', 'Image', 'Snippet']
  }
];

export const ComponentsListing: React.FC = () => {
  const navigate = useNavigate();

  const handleCategoryClick = (route: string) => {
    navigate(route);
  };

  const totalComponents = componentCategories.reduce((sum, category) => sum + category.componentCount, 0);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-text-primary mb-4">Component Library</h1>
          <p className="text-base md:text-xl text-text-secondary max-w-3xl mx-auto mb-6 px-4 text-center leading-relaxed">
            Explore our comprehensive collection of HeroUI-based React components. 
            Each category contains professionally crafted components with live examples, 
            documentation, and interactive previews.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <Chip color="primary" variant="flat" className="text-sm md:text-lg px-3 md:px-4 py-2">
              {totalComponents} Total Components
            </Chip>
            <Chip color="success" variant="dot" size="md" className="md:text-base">All Production Ready</Chip>
          </div>
        </div>

        {/* Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 px-4 md:px-0">
          {componentCategories.map((category) => {
            const IconComponent = category.icon;
            
            return (
              <Card
                key={category.id}
                isPressable
                onPress={() => handleCategoryClick(category.route)}
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <CardHeader>
                  <div className="flex items-start gap-3 w-full">
                    <div className="p-2 md:p-3 bg-primary/10 rounded-lg shrink-0">
                      <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg md:text-xl text-text-primary leading-tight">{category.title}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                        <Badge color="success" variant="flat" size="sm">
                          {category.componentCount} components
                        </Badge>
                        <Chip 
                          color={category.status === 'available' ? 'success' : 'warning'}
                          size="sm"
                          variant="flat"
                        >
                          {category.status === 'available' ? 'Available' : 'Coming Soon'}
                        </Chip>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardBody className="space-y-4">
                  <p className="text-text-secondary leading-relaxed">
                    {category.description}
                  </p>
                  
                  {/* Component List */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-text-primary">Components included:</p>
                    <div className="flex flex-wrap gap-1">
                      {category.features.slice(0, 6).map((feature) => (
                        <Chip key={feature} size="sm" variant="bordered" color="secondary" className="text-xs">
                          {feature}
                        </Chip>
                      ))}
                      {category.features.length > 6 && (
                        <Chip size="sm" variant="bordered" color="default" className="text-xs">
                          +{category.features.length - 6} more
                        </Chip>
                      )}
                    </div>
                  </div>
                </CardBody>
                
                <CardFooter>
                  <Button 
                    variant="bordered" 
                    color="primary"
                    onPress={() => handleCategoryClick(category.route)}
                    className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all"
                    endContent={<ArrowRight className="w-4 h-4" />}
                  >
                    Explore {category.title}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Additional Info Section */}
        <Card className="bg-linear-to-br from-primary/5 to-secondary/5">
          <CardBody>
            <h3 className="text-base md:text-lg font-semibold text-text-primary mb-3">🎨 Design System</h3>
            <p className="text-sm md:text-base text-text-secondary mb-4 leading-relaxed">
              All components are built on HeroUI foundations with our custom Tailwind v4 theme system, 
              ensuring consistency, accessibility, and beautiful design.
            </p>
            <div className="flex flex-wrap gap-2">
              <Chip size="sm" variant="flat" color="primary" className="text-xs">HeroUI Based</Chip>
              <Chip size="sm" variant="flat" color="secondary" className="text-xs">Tailwind v4</Chip>
              <Chip size="sm" variant="flat" color="success" className="text-xs">Dark Mode</Chip>
              <Chip size="sm" variant="flat" color="warning" className="text-xs">Accessible</Chip>
            </div>
          </CardBody>
        </Card>

    

      </div>
    </PlaygroundPageLayout>
  );
};